<template>
  <div class="container-fluid pt-4" style="margin-bottom: 5em;">

    <!-- Header -->
    <div class="row mb-3 align-items-center">
      <div class="col-md-6 col-sm-12">
        <h3 class="mb-0 text-primary">Inventory Intelligence</h3>
        <p class="text-muted mb-0">AI-powered analysis of your stock health</p>
      </div>
      <div class="col-md-6 col-sm-12 d-flex justify-content-end align-items-center">
        <span v-if="pipelineStatus" class="text-muted mr-3">
          <i class="bi bi-circle-fill mr-1" :class="pipelineStatusDotClass"></i>
          Pipeline: {{ pipelineStatus.status }}
        </span>
        <button class="btn btn-sm btn-outline-primary" @click="triggerAnalysis" :disabled="triggering">
          <b-spinner v-if="triggering" small /> Run Analysis
        </button>
        <button class="btn btn-sm btn-outline-secondary ml-2" @click="loadAll" :disabled="refreshing">
          <b-spinner v-if="refreshing" small />
          <i v-else class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    </div>

    <!-- Score + Summary Cards -->
    <div class="row mb-4" v-if="scoreLoaded">
      <!-- Health score -->
      <div class="col-md-3 col-6 mb-3">
        <div class="card h-100 border-0 shadow-sm text-center">
          <div class="card-body d-flex flex-column justify-content-center">
            <div class="display-4 font-weight-bold" :class="scoreColorClass">
              {{ score != null ? score.health_score : '—' }}
            </div>
            <div class="text-muted">Health Score</div>

            <div v-if="scoreGrade" class="mt-1">
              <span class="badge" :class="scoreGradeBadge">{{ scoreGrade }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Severity breakdown -->
      <div class="col-md-9 col-6 mb-3">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-body">
            <div class="row text-center">
              <div
                v-for="s in severityKeys"
                :key="s.key"
                class="col border-right last-no-border clickable"
                @click="setSeverityFilter(s.key)"
              >
                <div class="h4 font-weight-bold mb-0" :style="{ color: s.color }">
                  {{ summary[s.key] || 0 }}
                </div>
                <div class="text-muted">{{ s.key }}</div>
              </div>
            </div>
            <div class="mt-2">
              <div class="progress progress-xs">
                <div
                  v-for="s in severityKeys"
                  :key="s.key + '_bar'"
                  class="progress-bar"
                  :style="{ width: barWidth(s.key) + '%', background: s.color }"
                  :title="s.key + ': ' + (summary[s.key] || 0)"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!scoreLoaded && !scoreError" class="text-center py-4">
      <b-spinner variant="primary" /> Loading...
    </div>
    <div v-if="scoreError" class="alert alert-danger">{{ scoreError }}</div>

    <!-- Main content grid -->
    <div class="row">

      <!-- Concerns feed (left col) -->
      <div class="col-md-8 mb-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white d-flex align-items-center justify-content-between">
            <h6 class="mb-0 font-weight-bold">Active Concerns</h6>
            <div class="d-flex align-items-center">
              <!-- Severity filter -->
              <select class="form-control form-control-sm mr-2 filter-select-sm" v-model="filters.severity" @change="loadConcerns(1)">
                <option value="">All Severities</option>
                <option v-for="s in severityKeys" :key="s.key" :value="s.key">{{ s.key }}</option>
              </select>
              <!-- Category filter -->
              <select class="form-control form-control-sm mr-2 filter-select" v-model="filters.category" @change="loadConcerns(1)">
                <option value="">All Categories</option>
                <option value="STOCK_LEVEL">Stock Level</option>
                <option value="DATA_INTEGRITY">Data Integrity</option>
                <option value="VELOCITY">Velocity</option>
                <option value="FINANCIAL">Financial</option>
                <option value="EXPIRY">Expiry</option>
                <option value="OPERATIONAL">Operational</option>
              </select>
            </div>
          </div>
          <div class="card-body p-2">
            <div v-if="concernsLoading" class="text-center py-4">
              <b-spinner /> Loading concerns...
            </div>
            <div v-else-if="concerns.length === 0" class="text-center py-4 text-muted">
              <i class="bi bi-check-circle-fill text-success icon-xl"></i>
              <p class="mt-2 mb-0">No active concerns. Inventory looks healthy.</p>
            </div>
            <div v-else>
              <concern-card
                v-for="c in concerns"
                :key="c.id"
                :concern="c"

                :acting="actingConcernId === c.id"
                @click="openConcernDetail(c)"
                @view="openConcernDetail(c)"
                @snooze="quickSnooze(c)"
                @resolve="quickResolve(c)"
              />
            </div>

            <!-- Pagination -->
            <div v-if="concernsMeta && concernsMeta.last_page > 1" class="d-flex justify-content-center mt-2">
              <b-pagination
                :total-rows="concernsMeta.total"
                :per-page="concernsMeta.per_page"
                v-model="concernsPage"
                @change="loadConcerns"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="col-md-4 mb-4">

        <!-- At-Risk Products -->
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-header bg-white d-flex justify-content-between align-items-center">
            <h6 class="mb-0 font-weight-bold">At-Risk Products</h6>
            <span v-if="atRisk.length" class="badge badge-danger">{{ atRisk.length }}</span>
          </div>
          <div class="card-body p-0">
            <div v-if="atRiskLoading" class="text-center py-3"><b-spinner /></div>
            <div v-else-if="atRisk.length === 0" class="text-center py-3 text-muted">None right now.</div>
            <ul v-else class="list-group list-group-flush list-scrollable-lg">
              <li
                v-for="item in atRisk"
                :key="item.product_id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3 clickable"
                @click="openProductPanel(item)"
              >
                <div>
                  <div class="font-weight-semibold">{{ item.product_name || item.product_id }}</div>
                  <small class="text-muted">
                    {{ item.remaining_units }} left &bull; {{ item.days_of_stock_7d != null ? Math.round(item.days_of_stock_7d) + 'd stock' : '' }}
                  </small>
                </div>
                <span class="badge badge-danger badge-pill">{{ item.days_of_stock_7d != null ? Math.round(item.days_of_stock_7d) + 'd' : '!' }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Dead Stock -->
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-header bg-white d-flex justify-content-between align-items-center">
            <h6 class="mb-0 font-weight-bold">Dead Stock</h6>
            <span v-if="deadStock.length" class="badge badge-secondary">{{ deadStock.length }}</span>
          </div>
          <div class="card-body p-0">
            <div v-if="deadStockLoading" class="text-center py-3"><b-spinner /></div>
            <div v-else-if="deadStock.length === 0" class="text-center py-3 text-muted">No dead stock detected.</div>
            <ul v-else class="list-group list-group-flush list-scrollable-md">
              <li
                v-for="item in deadStock"
                :key="item.product_id"
                class="list-group-item d-flex justify-content-between align-items-center py-2 px-3"
              >
                <div>
                  <div class="font-weight-semibold">{{ item.product_name || item.product_id }}</div>
                  <small class="text-muted">
                    {{ item.days_since_last_sale != null ? item.days_since_last_sale + 'd since last sale' : 'No sales recorded' }}
                  </small>
                </div>
                <div class="text-right">
                  <div class="text-muted">{{ item.remaining_units }} units</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Anomalies -->
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white d-flex justify-content-between align-items-center">
            <h6 class="mb-0 font-weight-bold">Recent Anomalies</h6>
            <span v-if="anomalies.length" class="badge badge-warning">{{ anomalies.length }}</span>
          </div>
          <div class="card-body p-0">
            <div v-if="anomaliesLoading" class="text-center py-3"><b-spinner /></div>
            <div v-else-if="anomalies.length === 0" class="text-center py-3 text-muted">No anomalies detected.</div>
            <ul v-else class="list-group list-group-flush list-scrollable-md">
              <li v-for="a in anomalies" :key="a.id" class="list-group-item py-2 px-3">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="font-weight-semibold">{{ a.anomaly_type || a.type }}</div>
                    <small class="text-muted">{{ a.product_name || '' }}</small>
                  </div>
                  <span class="badge badge-warning badge-pill">{{ a.severity || 'HIGH' }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

      </div>
    </div>

    <!-- Concern detail modal -->
    <concern-detail-modal
      modal-id="dashboard-concern-detail"
      :concern="selectedConcern"
      @updated="onConcernUpdated"
      @hidden="selectedConcern = null"
    />

    <!-- Product intelligence panel -->
    <product-intelligence-panel
      modal-id="dashboard-product-panel"
      :product="selectedProduct"
      @hidden="selectedProduct = null"
    />

  </div>
</template>

<script>
import SeverityBadge from './SeverityBadge.vue';
import ConcernCard from './ConcernCard.vue';
import ConcernDetailModal from './ConcernDetailModal.vue';
import ProductIntelligencePanel from './ProductIntelligencePanel.vue';
import { SEVERITY_KEYS } from './constants.js';

export default {
  name: 'IntelligenceDashboard',
  components: { SeverityBadge, ConcernCard, ConcernDetailModal, ProductIntelligencePanel },
  data() {
    return {
      // Score
      score:      null,
      scoreGrade: null,
      summary:    {},
      scoreLoaded:false,
      scoreError: null,
      refreshing: false,

      // Concerns
      concerns:       [],
      concernsMeta:   null,
      concernsPage:   1,
      concernsLoading:false,
      actingConcernId:null,
      selectedConcern:null,

      filters: {
        severity: '',
        category: '',
      },

      // At-risk / dead stock / anomalies
      atRisk:           [],
      atRiskLoading:    false,
      deadStock:        [],
      deadStockLoading: false,
      anomalies:        [],
      anomaliesLoading: false,

      // Pipeline
      pipelineStatus: null,
      triggering:     false,

      // Product panel
      selectedProduct: null,

      severityKeys: SEVERITY_KEYS,
    };
  },
  computed: {
    scoreColorClass() {
      const hs = this.score?.health_score ?? null;
      if (hs == null) return 'text-muted';
      if (hs >= 80)   return 'text-success';
      if (hs >= 60)   return 'text-warning';
      return 'text-danger';
    },
    scoreGradeBadge() {
      const hs = this.score?.health_score ?? null;
      if (hs == null) return 'badge-secondary';
      if (hs >= 80)   return 'badge-success';
      if (hs >= 60)   return 'badge-warning';
      return 'badge-danger';
    },
    totalConcernCount() {
      return Object.values(this.summary).reduce((s, v) => s + (v || 0), 0);
    },
    pipelineStatusDotClass() {
      const s = this.pipelineStatus?.status;
      if (s === 'RUNNING') return 'text-warning';
      if (s === 'IDLE')    return 'text-success';
      return 'text-muted';
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.refreshing = true;
      await Promise.all([
        this.loadScore(),
        this.loadConcerns(1),
        this.loadAtRisk(),
        this.loadDeadStock(),
        this.loadAnomalies(),
        this.loadPipelineStatus(),
      ]);
      this.refreshing = false;
    },

    async loadScore() {
      this.scoreError = null;
      try {
        const res = await axios.get('/api/intelligence/score');
        const scoreObj = res.data?.score ?? null;
        this.score = scoreObj;

        if (!scoreObj) {
          this.scoreGrade = null;
          this.summary = {};
        } else {
          const hs = scoreObj.health_score ?? 0;
          if (hs >= 80)      this.scoreGrade = 'A';
          else if (hs >= 60) this.scoreGrade = 'B';
          else if (hs >= 40) this.scoreGrade = 'C';
          else               this.scoreGrade = 'F';

          this.summary = {
            CRITICAL: scoreObj.critical_concerns ?? 0,
            HIGH:     scoreObj.high_concerns ?? 0,
            MEDIUM:   scoreObj.medium_concerns ?? 0,
          };
        }

        this.scoreLoaded = true;
      } catch (e) {
        this.scoreError = 'Failed to load health score.';
        this.scoreLoaded = true;
      }
    },

    async loadConcerns(page = 1) {
      this.concernsPage    = page;
      this.concernsLoading = true;
      try {
        const params = { page };
        if (this.filters.severity) params.severity = this.filters.severity;
        if (this.filters.category) params.category = this.filters.category;
        const res = await axios.get('/api/intelligence/concerns', { params });
        this.concerns     = res.data.data || [];
        this.concernsMeta = res.data.meta || { total: res.data.total, per_page: res.data.per_page, last_page: res.data.last_page };
      } catch (_) {
        this.concerns = [];
      } finally {
        this.concernsLoading = false;
      }
    },

    async loadAtRisk() {
      this.atRiskLoading = true;
      try {
        const res = await axios.get('/api/intelligence/at-risk');
        this.atRisk = res.data.data || res.data.items || [];
      } catch (_) {
        this.atRisk = [];
      } finally {
        this.atRiskLoading = false;
      }
    },

    async loadDeadStock() {
      this.deadStockLoading = true;
      try {
        const res = await axios.get('/api/intelligence/dead-stock');
        this.deadStock = res.data.data || res.data.items || [];
      } catch (_) {
        this.deadStock = [];
      } finally {
        this.deadStockLoading = false;
      }
    },

    async loadAnomalies() {
      this.anomaliesLoading = true;
      try {
        const res = await axios.get('/api/intelligence/anomalies');
        this.anomalies = res.data.data || res.data.anomalies || [];
      } catch (_) {
        this.anomalies = [];
      } finally {
        this.anomaliesLoading = false;
      }
    },

    async loadPipelineStatus() {
      try {
        const res = await axios.get('/api/intelligence/pipeline/status');
        this.pipelineStatus = res.data;
      } catch (_) {}
    },

    async triggerAnalysis() {
      this.triggering = true;
      try {
        await axios.post('/api/intelligence/pipeline/trigger');
        this.$toasts.push({ type: 'success', message: 'Analysis triggered. Results will refresh shortly.' });
        setTimeout(() => this.loadAll(), 3000);
      } catch (e) {
        const msg = e?.response?.data?.message || 'Failed to trigger analysis. Try again in a moment.';
        this.$toasts.push({ type: 'error', message: msg });
      } finally {
        this.triggering = false;
      }
    },

    openConcernDetail(concern) {
      this.selectedConcern = concern;
      this.$bvModal.show('dashboard-concern-detail');
    },

    onConcernUpdated({ id }) {
      this.concerns = this.concerns.filter(c => c.id !== id);
      this.selectedConcern = null;
      this.loadScore();
    },

    async quickSnooze(concern) {
      this.actingConcernId = concern.id;
      try {
        await axios.patch(`/api/intelligence/concerns/${concern.id}/snooze`, { days: 7 });
        this.$toasts.push({ type: 'success', message: 'Concern snoozed for 7 days.' });
        this.concerns = this.concerns.filter(c => c.id !== concern.id);
        this.loadScore();
      } catch (_) {
        this.$toasts.push({ type: 'error', message: 'Failed to snooze concern.' });
      } finally {
        this.actingConcernId = null;
      }
    },

    async quickResolve(concern) {
      this.actingConcernId = concern.id;
      try {
        await axios.patch(`/api/intelligence/concerns/${concern.id}/resolve`);
        this.$toasts.push({ type: 'success', message: 'Concern resolved.' });
        this.concerns = this.concerns.filter(c => c.id !== concern.id);
        this.loadScore();
      } catch (_) {
        this.$toasts.push({ type: 'error', message: 'Failed to resolve concern.' });
      } finally {
        this.actingConcernId = null;
      }
    },

    setSeverityFilter(severity) {
      this.filters.severity = severity === this.filters.severity ? '' : severity;
      this.loadConcerns(1);
    },

    openProductPanel(item) {
      this.selectedProduct = { id: item.product_id, product: item.product_name || item.product_id };
      this.$bvModal.show('dashboard-product-panel');
    },

    barWidth(key) {
      if (!this.totalConcernCount) return 0;
      return ((this.summary[key] || 0) / this.totalConcernCount) * 100;
    },
  },
};
</script>

<style scoped>
.last-no-border > div:last-child {
  border-right: none !important;
}
.font-weight-semibold {
  font-weight: 600;
}
.clickable {
  cursor: pointer;
}
.progress-xs {
  height: 6px;
}
.icon-xl {
  font-size: 2rem;
}
.filter-select-sm {
  width: 120px;
}
.filter-select {
  width: 130px;
}
.list-scrollable-lg {
  max-height: 250px;
  overflow-y: auto;
}
.list-scrollable-md {
  max-height: 200px;
  overflow-y: auto;
}
</style>
